import { Button } from "@/components/ui/button";
import { Copy, Trash, Loader, Save, BookOpen } from "lucide-react";
import { TemplateActionsProps } from "../types";
import TemplateTestDialog from "../TemplateTestDialog";
import { openDocumentation } from "@/services/docsService";
import { useTranslation } from "react-i18next";

const TemplateActions = ({
  mode,
  template,
  formData,
  onSave,
  onDelete,
  onDuplicate,
  isSaving,
  isDeleting,
  isUnsaved,
  permissions,
}: TemplateActionsProps) => {
  const { t } = useTranslation();
  const handleOpenDocs = () => {
    openDocumentation("TEMPLATE");
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/30 bg-gradient-to-r from-muted/20 to-transparent rounded-lg -mx-2 px-2 py-2">
      {/* Основной контейнер с кнопками */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Основная кнопка сохранения - только для тех, кто может редактировать */}
          {permissions.canEdit && (
            <Button
              type="button"
              onClick={onSave}
              disabled={
                !permissions.canEdit ||
                !formData.description ||
                formData.description.length == 0 ||
                !formData.templateName ||
                formData.templateName.length == 0 ||
                isSaving ||
                (mode === "edit" && !isUnsaved && !template?.isSystem) // Кнопка неактивна для сохранённых пользовательских шаблонов
              }
              size="sm"
              className="w-12 sm:w-auto"
            >
              {isSaving ? (
                <>
                  <Loader size={14} className="animate-spin" />
                  <span className="hidden sm:inline ml-1.5">
                    {mode === "create" ? t('templates.actions.creating') : t('templates.actions.updating')}
                  </span>
                </>
              ) : mode === "create" ? (
                <>
                  <Save size={14} />
                  <span className="hidden sm:inline ml-1.5">{t('templates.actions.save_btn')}</span>
                </>
              ) : (
                <>
                  <Save size={14} />
                  <span className="hidden sm:inline ml-1.5">
                    {isUnsaved ? t('templates.actions.save_changes_btn') : t('templates.actions.saved')}
                  </span>
                </>
              )}
            </Button>
          )}

          {/* Кнопка тестирования - доступна всем, у кого есть разрешение */}
          {permissions.canTest && (
            <TemplateTestDialog
              promptText={formData.prompt}
              disabled={formData.prompt.trim().length === 0}
            />
          )}

          {/* Кнопка копирования - доступна для существующих шаблонов, в режиме создания показывается неактивной */}
          {permissions.canCopy && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDuplicate}
              disabled={!template || isSaving}
              className="w-12 sm:w-auto"
            >
              <Copy size={14} />
              <span className="hidden sm:inline ml-1.5">{t('templates.actions.duplicate_btn')}</span>
            </Button>
          )}

          {/* Кнопка удаления - доступна для существующих шаблонов, в режиме создания показывается неактивной */}
          {(template || mode === "create") && (template?.isSystem || permissions.canDelete) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 w-12 sm:w-auto"
              onClick={onDelete}
              disabled={!template || !permissions.canDelete || isDeleting}
            >
              {isDeleting ? (
                <Loader size={14} className="animate-spin" />
              ) : (
                <Trash size={14} />
              )}
              <span className="hidden sm:inline ml-1.5">{t('templates.actions.delete_btn')}</span>
            </Button>
          )}
        </div>

        {/* Кнопка инструкции - всегда отображается и прижата к правому краю */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleOpenDocs}
          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200 w-12 sm:w-auto"
        >
          <BookOpen size={14} />
          <span className="hidden sm:inline ml-1.5">{t('templates.actions.help_btn')}</span>
        </Button>
      </div>

    </div>
  );
};

export default TemplateActions;
